import React, { useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { CacheLabel } from '@/components/CacheLabel'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Field, FieldLabel } from '@/components/ui/field'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ClientLeverageType } from 'api/Clients/Types/ClientLeverageType'
import { TradingAccountStatus } from 'api/Clients/Types/TradingAccount'
import { TradingAccountDetailDto } from 'api/Clients/Types/TradingAccountDetails'
import { UpdateTradingAccount } from 'api/Clients/Types/UpdateTradingAccount'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AccountGroupBasicDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { IntroducingBrokerBasicDto } from 'api/Trading-Accounts/Types/IntroducingBrokerBasicDto'
import { LeverageType } from 'api/Trading-Accounts/Types/LeverageType'
import { MarketingCampaignDto } from 'api/Trading-Accounts/Types/MarketingCampaignDto'
import { PlatformTypeId } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { WalletType } from 'api/Wallet/Types/Wallet'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown, Info } from 'lucide-react'
import { INTRODUCING_BROKERS_CACHE_MESSAGE } from 'utils/cache'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'
import { v4 as uuid } from 'uuid'

import { CampaignDetailsBusinessRuleModal } from '../../../Marketing/CampaignDetailsPage/CampaignDetailsBusinessRuleModal'
import {
  BusinessRule,
  CampaignDetailsBusinessRulesTable,
} from '../../../Marketing/CampaignDetailsPage/CampaignDetailsBusinessRulesTable'
import { TransactionTabType } from '../../../Transactions/Create/TransactionTab'
import { AccountGroupDetailsBlock } from './Form/AccountGroupDetailsBlock'

interface TradingAccountDetailsFormValues {
  client: {
    id: string
    name: string
    affiliateToken: string | null
  }
  wallet: {
    id: string
    name: string
  }
  walletType: NameDto
  tickmillCompany: NameDto
  accountGroupId: string
  introducingBroker: {
    id: string
    name: string
    client?: {
      id: string
      name: string
    }
    introducingBrokerProfile?: {
      id: string
      name: string
    }
  } | null
  leverageType: { id: number; name: string }
  receivesReports: boolean
  isExcludedFromArchiving: boolean
  statusTypeId: number
  campaignId: string | null
  externalId: string | null
  server: string
  platformType: string
  tradingAccountGroupType: string
  accountGroupType: string
  bookType: string | undefined
  classification: string
  currency: string
  isTradingViewConnected: boolean | null
  country: string | undefined
  isEnabled: boolean
  isSwapFree: boolean
  canBeAttached: boolean
  canBeDirectAssigned: boolean
  canBeIBAssigned: boolean
  restrictions: number[]
  accountGroupRestrictions: number[]
  campaignRestrictions: string[]
  createdDate: string
  businessRules: BusinessRule[]
  campaignBusinessRules: BusinessRule[]
  manualOverride: boolean
}

interface ModalDataProps<T> {
  data: T | undefined
  visible: boolean
}

type TradingAccountDetailsFormProps = FormikProps<TradingAccountDetailsFormValues> & OuterProps

const DetailCheckboxField: React.FC<{
  label: React.ReactNode
  name: string
  checked: boolean
  readOnly?: boolean
  disabled?: boolean
  onChange?: (checked: boolean) => void
  dataTest?: string
}> = ({ label, name, checked, readOnly, disabled, onChange, dataTest }) => (
  <Field orientation='horizontal' className='w-full items-center gap-3'>
    <Checkbox
      id={name}
      name={name}
      checked={checked}
      disabled={disabled}
      readOnly={readOnly}
      onCheckedChange={(nextChecked) => {
        if (!disabled && !readOnly) {
          onChange?.(Boolean(nextChecked))
        }
      }}
      data-test={dataTest}
    />
    <FieldLabel htmlFor={name}>{label}</FieldLabel>
  </Field>
)

const StatusWarningBadge = () => (
  <Tooltip>
    <TooltipTrigger>
      <Info size={14} style={{ marginLeft: '0.25rem' }} />
    </TooltipTrigger>
    <TooltipContent side='top'>Trading account status is not approved.</TooltipContent>
  </Tooltip>
)

const useCampaignBusinessRuleModal = (props: TradingAccountDetailsFormProps) => {
  const { values, setFieldValue } = props

  const initialModalDataState = {
    data: undefined,
    visible: false,
  }

  const [campaignBusinessRuleModal, setCampaignBusinessRuleModal] =
    useState<ModalDataProps<BusinessRule>>(initialModalDataState)

  const handleCampaignBusinessRuleSubmit = (businessRule?: BusinessRule) => {
    if (businessRule) {
      const isNew = !values.businessRules.find((x) => x?.id === businessRule?.id)
      const newBusinessRules = isNew
        ? values.businessRules.concat(businessRule)
        : values.businessRules.map((x) => (x.id === businessRule.id ? businessRule : x))
      setFieldValue('businessRules', newBusinessRules)
      setCampaignBusinessRuleModal(initialModalDataState)
    }
  }

  const handleCampaignBusinessRuleModalRemove = (businessRule?: BusinessRule) => {
    if (businessRule) {
      const newBusinessRules = values.businessRules.filter((x) => x.id !== businessRule.id)
      setFieldValue('businessRules', newBusinessRules)
      setCampaignBusinessRuleModal(initialModalDataState)
    }
  }

  const handleCampaignBusinessRuleModalOpen = (businessRule?: BusinessRule) => {
    setCampaignBusinessRuleModal({
      data: businessRule,
      visible: true,
    })
  }

  const handleCampaignBusinessRuleModalClose = () => {
    setCampaignBusinessRuleModal(initialModalDataState)
  }

  return {
    data: campaignBusinessRuleModal,
    handleCampaignBusinessRuleSubmit,
    handleCampaignBusinessRuleModalOpen,
    handleCampaignBusinessRuleModalRemove,
    handleCampaignBusinessRuleModalClose,
  }
}

const TradingAccountDetailsFormUI: React.FC<TradingAccountDetailsFormProps> = (props) => {
  const {
    resetForm,
    handleSubmit,
    setFieldValue,
    setSubmitting,
    errors,
    isEdit,
    setEdit,
    values,
    permissions,
    data,
    campaignBusinessRuleTypes = [],
  } = props

  const navigate = useNavigate()

  const campaignBusinessRuleModal = useCampaignBusinessRuleModal(props)
  const createTransactionPath =
    values.walletType.id === WalletType.ETD
      ? links.createETDTransaction.url
      : links.createTransaction.url

  const availableLeverageTypes = useMemo(
    () => uniqueLeverageTypes([...props.leverageType, values.leverageType]).sort(sortLeverageTypes),
    [props.leverageType, values.leverageType]
  )

  const introducingBrokerOptions = useMemo(() => {
    const currentIntroducingBroker = values.introducingBroker
    if (
      !currentIntroducingBroker ||
      props.introducingBrokers.some((ib) => ib.id === currentIntroducingBroker.id)
    ) {
      return props.introducingBrokers
    }

    return [
      { id: currentIntroducingBroker.id, name: currentIntroducingBroker.name },
      ...props.introducingBrokers,
    ]
  }, [props.introducingBrokers, values.introducingBroker])

  const campaignRestrictionOptions = useMemo(
    () =>
      values.campaignRestrictions.map((restriction) => ({
        name: restriction,
        id: restriction,
      })),
    [values.campaignRestrictions]
  )

  const selectedAccountGroup = useMemo(
    () =>
      props.accountGroups.find((accountGroup) => accountGroup.id === values.accountGroupId) ??
      props.data.accountGroup,
    [props.accountGroups, props.data.accountGroup, values.accountGroupId]
  )

  const isStatusNotApproved = Number(values.statusTypeId) !== TradingAccountStatus.Approved
  const statusRightAddon = isStatusNotApproved ? <StatusWarningBadge /> : undefined

  const handleSubmitForm = () => {
    setSubmitting(true)
    setEdit(false)
    handleSubmit()
  }

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleResetMasterPassword = async () => {
    await props.resetPassword({ withInvestorPassword: false })
  }

  const handleResetInvestorPassword = async () => {
    await props.resetPassword({ withInvestorPassword: true })
  }

  const renderMenu = () => (
    <>
      <DropdownMenuItem
        onSelect={() => props.setAuditLogModal(true)}
        data-test='audit-log-menu-button'
      >
        Audit Log
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanResetTradingAccountPassword)}
        onSelect={handleResetMasterPassword}
        data-test='reset-master-password-menu-button'
      >
        Reset Master Password
      </DropdownMenuItem>
      {data.accountGroup.platformType.id !== PlatformTypeId.TickmillTrader && (
        <DropdownMenuItem
          disabled={!hasPermission(permissions, PermissionsEnum.CanResetTradingAccountPassword)}
          onSelect={handleResetInvestorPassword}
          data-test='reset-investor-password-menu-button'
        >
          Reset Investor Password
        </DropdownMenuItem>
      )}
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanArchiveTradingAccounts)}
        onSelect={() =>
          props.setConfirmationModal({
            visible: true,
            action: 'toggleExcludeFromArchiving',
            onSuccess: props.forceRefresh,
          })
        }
        data-test='toggle-exclude-from-archiving-menu-button'
      >
        Toggle Exclude From Archiving
      </DropdownMenuItem>

      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanArchiveTradingAccounts)}
        onSelect={() =>
          props.setConfirmationModal({
            visible: true,
            action: 'archiveAccount',
            onSuccess: props.forceRefresh,
          })
        }
        data-test='archive-account-menu-button'
      >
        Archive Account
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>Create Transaction</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem
            onSelect={() =>
              navigate(createTransactionPath, {
                state: {
                  tradingAccount: data,
                  client: values.client,
                  wallet: values.wallet,
                  tab: TransactionTabType.FROM_WALLET_TO_TRADING_ACCOUNT_TRANSACTION,
                },
              })
            }
          >
            Transfer In
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              navigate(createTransactionPath, {
                state: {
                  tradingAccount: data,
                  client: values.client,
                  tab: TransactionTabType.FROM_TRADING_ACCOUNT_TO_WALLET_TRANSACTION,
                },
              })
            }
          >
            Transfer Out
          </DropdownMenuItem>
          {values.walletType.id === WalletType.ETD && (
            <DropdownMenuItem
              onSelect={() =>
                navigate(links.createETDTransaction.url, {
                  state: {
                    tradingAccount: data,
                    client: values.client,
                    tab: TransactionTabType.CONVERSION,
                  },
                })
              }
            >
              Conversion
            </DropdownMenuItem>
          )}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  )

  return (
    <React.Fragment>
      {campaignBusinessRuleModal.data && (
        <Dialog
          open={campaignBusinessRuleModal.data.visible}
          onOpenChange={(open) => {
            if (!open) {
              campaignBusinessRuleModal.handleCampaignBusinessRuleModalClose()
            }
          }}
        >
          <CampaignDetailsBusinessRuleModal
            data={campaignBusinessRuleModal.data.data}
            businessRules={values.businessRules}
            campaignBusinessRuleTypes={campaignBusinessRuleTypes}
            onSubmit={campaignBusinessRuleModal.handleCampaignBusinessRuleSubmit}
          />
        </Dialog>
      )}

      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {!isEdit ? (
                <Button
                  type='button'
                  onClick={() => setEdit(true)}
                  disabled={
                    props.isSubmitting ||
                    !hasPermission(props.permissions, PermissionsEnum.CanEditTradingAccount)
                  }
                  data-test='edit-button'
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant='outline'
                    type='button'
                    onClick={handleCancel}
                    disabled={props.isSubmitting}
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    onClick={handleSubmitForm}
                    disabled={props.isSubmitting}
                    data-test='save-button'
                  >
                    Save
                    {props.isSubmitting && <Spinner data-icon='inline-start' />}
                  </Button>
                </>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='More options'
                      data-test='more-button'
                      disabled={props.isSubmitting}
                    />
                  }
                >
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard columns={2} title='Main'>
              <FormReadonlyField
                label='Client'
                value={values.client.name}
                link={generatePath(links.recordsClients.detailsUrl as string, {
                  id: values.client.id,
                  slug: 'details',
                })}
              />

              <FormReadonlyField
                label='Wallet'
                value={values.wallet.name}
                link={generatePath(links.recordsWallets.detailsUrl as string, {
                  id: values.wallet.id,
                  slug: 'details',
                })}
              />
              <FormSelectField
                options={props.accountGroups}
                name='accountGroupId'
                onChange={(value) => setFieldValue('accountGroupId', value)}
                label='Account Group'
                readOnly={!isEdit}
                error={errors.accountGroupId}
                link={generatePath(links.recordsAccountGroups.detailsUrl as string, {
                  id: selectedAccountGroup.id,
                  slug: 'details',
                })}
              />

              <FormReadonlyField
                label='Date Created'
                name='createdDate'
                value={values.createdDate}
              />

              <FormSelectField
                name='introducingBroker.id'
                label={
                  <CacheLabel
                    label='Introducing Broker'
                    message={INTRODUCING_BROKERS_CACHE_MESSAGE}
                    ariaLabel='Introducing Broker cache information'
                  />
                }
                options={introducingBrokerOptions}
                value={values.introducingBroker?.id}
                readOnly={!isEdit}
                allowClear
                showSearch
                link={
                  !isEdit && values.introducingBroker?.id
                    ? generatePath(links.recordsIntroducingBrokers.detailsUrl as string, {
                        id: values.introducingBroker.id,
                        slug: 'details',
                      })
                    : undefined
                }
                onChange={(value) => {
                  const ib = props.introducingBrokers.find((x) => x.id === value)
                  setFieldValue('introducingBroker', ib ? { id: ib.id, name: ib.name } : null)
                }}
              />

              <FormReadonlyField
                label='Affiliate ID'
                name='client.affiliateToken'
                value={values.client.affiliateToken?.toString()}
              />

              <FormSelectField
                options={availableLeverageTypes}
                label='Leverage'
                readOnly={!isEdit}
                name='leverageType'
              />

              <DetailCheckboxField
                label='Receive reports'
                name='receivesReports'
                checked={values.receivesReports}
                readOnly={!isEdit}
                onChange={(checked) => setFieldValue('receivesReports', checked)}
                dataTest='receivesReports-field'
              />
              <DetailCheckboxField
                label='Is Excluded From Archiving'
                name='isExcludedFromArchiving'
                checked={values.isExcludedFromArchiving}
                readOnly
                dataTest='excluded-from-archiving-field'
              />
              <FormSelectField
                options={props.tradingAccountStatus}
                label='Status'
                name='statusTypeId'
                onChange={(value) => setFieldValue('statusTypeId', Number(value))}
                readOnly={!isEdit}
                error={errors.statusTypeId}
                rightAddon={statusRightAddon}
              />
              <FormSelectField
                options={props.marketingCampaigns}
                label='Campaign'
                readOnly={!isEdit}
                name='campaignId'
                error={errors.campaignId}
                onChange={(value) => {
                  setFieldValue('campaignId', value ? String(value) : null)
                }}
              />

              <FormReadonlyField
                label='External ID'
                name='externalId'
                value={values.externalId?.toString()}
              />
            </DataCard>

            <DataCard columns={2} title='Platform Details'>
              <FormReadonlyField label='Server' name='server' value={values.server} />
              <FormReadonlyField
                label='Platform'
                name='platformType'
                value={props.data.platformType?.name ?? values.platformType}
              />
              {props.data.parentPlatformType && (
                <FormReadonlyField
                  label='Parent Platform'
                  name='parentPlatformType'
                  value={props.data.parentPlatformType.name}
                />
              )}
              {values.isTradingViewConnected === null ? (
                <FormReadonlyField label='TV' value='N/A' />
              ) : (
                <DetailCheckboxField
                  label='TV'
                  name='isTradingViewConnected'
                  checked={Boolean(values.isTradingViewConnected)}
                  disabled
                  dataTest='trading-view-connected-field'
                />
              )}
            </DataCard>

            <DataCard title='Restrictions'>
              <FormMultiSelectField
                options={props.tradingAccountRestrictions}
                label='Restrictions'
                readOnly={!isEdit}
                showSearch
                name='restrictions'
                onChange={(restrictionValues) => {
                  const nextRestrictions = Array.isArray(restrictionValues)
                    ? restrictionValues.map((value) => Number(value))
                    : restrictionValues == null
                      ? []
                      : [Number(restrictionValues)]

                  setFieldValue('restrictions', nextRestrictions)
                }}
              />
            </DataCard>

            <DataCard title='Business Rules'>
              <CampaignDetailsBusinessRulesTable
                businessRules={values.businessRules}
                isEdit={isEdit}
                onAdd={campaignBusinessRuleModal.handleCampaignBusinessRuleModalOpen}
                onEdit={campaignBusinessRuleModal.handleCampaignBusinessRuleModalOpen}
                onRemove={campaignBusinessRuleModal.handleCampaignBusinessRuleModalRemove}
              />
            </DataCard>

            <DataCard title='Swap Free Option'>
              <DetailCheckboxField
                label='Manual Override'
                name='manualOverride'
                checked={values.manualOverride}
                readOnly={!isEdit}
                disabled={!hasPermission(permissions, PermissionsEnum.CanEditSwapFreeOption)}
                onChange={(checked) => setFieldValue('manualOverride', checked)}
                dataTest='manual-override-field'
              />
            </DataCard>
          </DataColumn>

          <DataColumn>
            <AccountGroupDetailsBlock />

            <DataCard title='Account Group Restrictions'>
              <FormMultiSelectField
                options={props.tradingAccountRestrictions}
                label='Restrictions'
                showSearch
                name='accountGroupRestrictions'
                placeholder='No restrictions'
                readOnly
              />
            </DataCard>

            <DataCard title='Campaign Restrictions'>
              <FormMultiSelectField
                options={campaignRestrictionOptions}
                label='Restrictions'
                name='campaignRestrictions'
                placeholder='No restrictions'
                readOnly
              />
            </DataCard>

            <DataCard title='Campaign Business Rules'>
              <CampaignDetailsBusinessRulesTable
                businessRules={values.campaignBusinessRules}
                isEdit={false}
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </React.Fragment>
  )
}

export const uniqueLeverageTypes = (leverageTypes: ClientLeverageType[]): ClientLeverageType[] => {
  return Array.from(leverageTypes)
    .map((x) => ({ id: Number(x.id), name: x.name }))
    .reduce((acc: ClientLeverageType[], current) => {
      if (!acc.find((x) => x.id === current.id)) {
        return acc.concat([current])
      }
      return acc
    }, [])
}

export const sortLeverageTypes = (
  f1: ClientLeverageType | LeverageType,
  f2: ClientLeverageType | LeverageType
): number => {
  const [, name1] = f1.name.split(':')
  const [, name2] = f2.name.split(':')

  return Number.parseInt(name1, 10) - Number.parseInt(name2, 10)
}

const dataToSubmit = (
  values: TradingAccountDetailsFormValues
): FormSubmitValues<UpdateTradingAccount> => {
  return {
    accountGroupId: values.accountGroupId,
    campaignId: values.campaignId || null,
    introducingBrokerId: values.introducingBroker ? values.introducingBroker.id : null,
    leverageTypeId: values.leverageType.id,
    receivesReports: values.receivesReports,
    restrictions: values.restrictions,
    businessRules: values.businessRules.map((x) => ({
      businessRuleId: x.rule.id,
      businessRuleValue: x.value,
    })),
    tradingAccountStatusId: values.statusTypeId,
    walletId: values.wallet.id,
    manualOverride: values.manualOverride,
    automaticClosure: false,
  }
}

interface OuterProps {
  data: TradingAccountDetailDto
  isEdit: boolean
  accountGroups: AccountGroupBasicDto[]
  tradingAccountStatus: NameDto[]
  tradingAccountRestrictions: RestrictionDto[]
  introducingBrokers: IntroducingBrokerBasicDto[]
  leverageType: ClientLeverageType[]
  marketingCampaigns: MarketingCampaignDto[]
  campaignBusinessRuleTypes: NameDto[]
  permissions: PermissionDto[]

  setEdit(val: boolean): void
  onSubmit(values: FormSubmitValues<UpdateTradingAccount>): Promise<void>
  resetPassword({ withInvestorPassword }: { withInvestorPassword?: boolean }): void
  setAuditLogModal(val: boolean): void
  setConfirmationModal(val: {
    visible: boolean
    action: 'toggleExcludeFromArchiving' | 'archiveAccount'
    onSuccess?: () => void
  }): void
  forceRefresh(): void
}

export const TradingAccountDetailsForm = withFormik<OuterProps, TradingAccountDetailsFormValues>({
  mapPropsToValues: ({ data, marketingCampaigns }) => {
    console.log({ data, marketingCampaigns })
    return {
      client: data.client,
      wallet: data.wallet,
      walletType: data.walletType,
      tickmillCompany: data.tickmillCompany,
      accountGroupId: data.accountGroup.id,
      createdDate: formatLocalDateUTC(data.createdDate, true) || '',
      introducingBroker: data.introducingBroker,
      leverageType: data.leverageType,
      receivesReports: data.receivesReports,
      isExcludedFromArchiving: data.isExcludedFromArchiving,
      statusTypeId: data.status.id,
      campaignId: data.campaign?.id || null,
      externalId: data.externalId || null,
      restrictions: data.restrictions?.map((x) => x.id) || [],
      server: data.server.name,
      platformType: data.accountGroup.platformType.name,
      isTradingViewConnected: data.isTradingViewConnected,
      tradingAccountGroupType: data.accountGroup.tradingAccountType.name,
      accountGroupType: data.accountGroup.accountGroupType.name,
      bookType: data.accountGroup.bookType?.name,
      classification: data.accountGroup.classificationType.name,
      currency: data.accountGroup.currency.id,
      country: data.accountGroup.country?.name,
      isEnabled: data.accountGroup.isEnabled,
      isSwapFree: data.accountGroup.isSwapFree,
      canBeAttached: data.accountGroup.canBeAttached,
      canBeDirectAssigned: data.accountGroup.canBeDirectAssigned,
      canBeIBAssigned: data.accountGroup.canBeIBAssigned,
      accountGroupRestrictions: data.accountGroup.restrictions?.map((x) => x.id) || [],
      campaignRestrictions: data.campaign?.restrictions.map((x) => x.name) || [],
      businessRules: (data?.businessRules || []).map((x) => ({
        id: uuid(),
        type: {
          id: x.businessRule.businessRuleType.id,
          name: x.businessRule.businessRuleType.name,
        },
        rule: { id: x.businessRule.id, name: x.businessRule.name },
        value: x.businessRuleValue || null,
      })),
      campaignBusinessRules: (data.campaign?.businessRules || []).map((x) => ({
        id: uuid(),
        type: {
          id: x.businessRule.businessRuleType.id,
          name: x.businessRule.businessRuleType.name,
        },
        rule: { id: x.businessRule.id, name: x.businessRule.name },
        value: x.businessRuleValue || null,
      })),
      manualOverride: data.manualOverride,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<TradingAccountDetailsFormValues> = {}
    if (!values.statusTypeId) {
      errors.statusTypeId = 'required'
    }
    return errors
  },
  enableReinitialize: false,
})(TradingAccountDetailsFormUI)
